import type {TranslationKey} from '_metronic/i18n/translationKeys'

import type {CommentFormValues, GridFilters, RowComments, StudentBlock, TopFilters} from './types'

/** FR-37: comment text is plain text, up to 2,000 characters. */
export const MAX_COMMENT_LENGTH = 5000

/** FR-08: students per page. */
export const PAGE_SIZES = [25, 50, 100] as const
export const DEFAULT_PAGE_SIZE = 25

export const EMPTY_TOP_FILTERS: TopFilters = {
  classIds: [],
  peopleIds: [],
  studentIds: [],
  dateFrom: '',
  dateTo: '',
}

export const EMPTY_GRID_FILTERS: GridFilters = {subjectNames: [], teacherIds: [], hideEmpty: false}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Calendar pickers on this page (the portal's flatpickr DatePicker), in the
 * `YYYY-MM-DD` the API takes. No `altInput`: it would hide the real input and
 * leave the field's <label> pointing at nothing. Defined once so the options
 * object is stable and an open calendar is never rebuilt.
 */
export const DATE_PICKER_OPTIONS = {
  dateFormat: 'Y-m-d',
  allowInput: false,
} as const

/**
 * A date the way the API takes it: `YYYY-MM-DD`, and a real calendar day —
 * 2026-02-30 is not one, nor is a two-digit year. Dates on this page are typed
 * as plain text (FR-36), so this is the only thing between a typo and the API.
 * Same rule as the College Counselling editable tables.
 */
export const isValidIsoDate = (text: string): boolean => {
  const match = ISO_DATE.exec(String(text ?? '').trim())
  if (!match) return false
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  if (year < 1900 || year > 2200) return false
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

/**
 * Whether a `YYYY-MM-DD` comment date falls in the Comments date filter; an
 * empty end is open. Same rule the server applies to subject comments.
 */
export const isInDateRange = (date: string, dateFrom: string, dateTo: string): boolean => {
  const from = String(dateFrom ?? '').trim()
  const to = String(dateTo ?? '').trim()
  return (!from || date >= from) && (!to || date <= to)
}

/** Today in the browser's own time zone, as `YYYY-MM-DD`. */
export const todayIso = (): string => {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/**
 * A fresh single-use key for one opening of the add form (FR-46). The form
 * keeps it across retries, so a double click or a retry after a dropped
 * connection cannot save the comment twice.
 */
export const newClientKey = (): string => {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // RFC 4122 v4 shape from getRandomValues, else Math.random as a last resort
  // (old WebViews without randomUUID still reach this page from the mobile app).
  const bytes = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes)
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * `2026-09-18` → `18 Sep 2026` (English) or the locale's own short date. The
 * date is built in local time so it never shifts a day across time zones.
 */
export const formatCommentDate = (iso: string, locale = 'en'): string => {
  const match = ISO_DATE.exec(String(iso ?? '').trim())
  if (!match) return iso
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  const options: Intl.DateTimeFormatOptions = {day: 'numeric', month: 'short', year: 'numeric'}
  try {
    if (locale.startsWith('en')) {
      // Day-first like the rest of the portal, with the plain "Sep" (en-GB says "Sept").
      const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date)
      const part = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
      return `${part('day')} ${part('month')} ${part('year')}`
    }
    return new Intl.DateTimeFormat(locale, options).format(date)
  } catch {
    return iso
  }
}

export type CommentFormErrors = Partial<Record<keyof CommentFormValues, TranslationKey>>

/**
 * FR-32 / FR-36 / FR-37: date (a real YYYY-MM-DD day), faculty member and text
 * are all required; text is at most 2,000 characters.
 */
export const validateCommentForm = (values: CommentFormValues): CommentFormErrors => {
  const errors: CommentFormErrors = {}
  if (!String(values.date ?? '').trim()) errors.date = 'STUDENT_COMMENTS.FORM.DATE_REQUIRED'
  else if (!isValidIsoDate(values.date)) errors.date = 'STUDENT_COMMENTS.FORM.DATE_INVALID'
  if (!String(values.facultyUserId ?? '').trim())
    errors.facultyUserId = 'STUDENT_COMMENTS.FORM.FACULTY_REQUIRED'
  const text = String(values.text ?? '')
  if (!text.trim()) errors.text = 'STUDENT_COMMENTS.FORM.TEXT_REQUIRED'
  else if (text.length > MAX_COMMENT_LENGTH) errors.text = 'STUDENT_COMMENTS.FORM.TEXT_TOO_LONG'
  return errors
}

export type DateRangeErrors = Partial<Record<'dateFrom' | 'dateTo', TranslationKey>>

/** FR-24: either end may be blank; a filled one must be a real date, and From ≤ To. */
export const validateDateRange = (dateFrom: string, dateTo: string): DateRangeErrors => {
  const errors: DateRangeErrors = {}
  const from = String(dateFrom ?? '').trim()
  const to = String(dateTo ?? '').trim()
  if (from && !isValidIsoDate(from)) errors.dateFrom = 'STUDENT_COMMENTS.FILTER.DATE_INVALID'
  if (to && !isValidIsoDate(to)) errors.dateTo = 'STUDENT_COMMENTS.FILTER.DATE_INVALID'
  if (!errors.dateFrom && !errors.dateTo && from && to && from > to)
    errors.dateTo = 'STUDENT_COMMENTS.FILTER.DATE_ORDER'
  return errors
}

/** The meeting-date part every row request repeats, so the server filters the same way. */
export const dateRangeBody = (top: Pick<TopFilters, 'dateFrom' | 'dateTo'>) => ({
  dateFrom: top.dateFrom.trim() || null,
  dateTo: top.dateTo.trim() || null,
})

/** POST student-comments body. Empty filters are left out: they impose no restriction (FR-21). */
export const buildGridBody = (
  page: number,
  pageSize: number,
  top: TopFilters,
  grid: GridFilters
) => ({
  page,
  pageSize,
  ...(top.classIds.length ? {classIds: top.classIds} : {}),
  ...(top.peopleIds.length ? {peopleIds: top.peopleIds} : {}),
  ...(top.studentIds.length ? {studentIds: top.studentIds} : {}),
  ...dateRangeBody(top),
  ...(grid.subjectNames.length ? {subjectNames: grid.subjectNames} : {}),
  ...(grid.teacherIds.length ? {teacherIds: grid.teacherIds} : {}),
  hideEmpty: grid.hideEmpty,
})

/**
 * Replaces one row's comments (FR-38) without touching the rest of the page.
 * `subjectId` null is the General row.
 */
export const replaceRowComments = (
  students: StudentBlock[],
  studentId: number,
  subjectId: number | null,
  row: RowComments
): StudentBlock[] =>
  students.map((student) => {
    if (student.id !== studentId) return student
    if (subjectId === null) return {...student, general: {comments: row.comments ?? []}}
    return {
      ...student,
      subjects: student.subjects.map((s) =>
        s.subjectId === subjectId
          ? {...s, comments: row.comments ?? [], totalInSession: row.totalInSession ?? 0}
          : s
      ),
    }
  })

/** Error envelope from fetchData's onFailure: an API answer or a thrown error. */
export type ApiFailure = {
  statusCode?: number
  message?: string
  result?: any
  response?: any
} | null

/** The newer copy of a comment a 409 hands back (`result.current`). */
export const conflictCurrent = (failure: ApiFailure) =>
  failure?.statusCode === 409
    ? (failure.result?.current ?? failure.response?.current ?? null)
    : null
